import React, { useEffect, useState, useImperativeHandle, useLayoutEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { sendAnalyticsEvents, sendPurchaseEvent } from '../../analytics/common';
import { PLANS } from '../../analytics/eCommerce';
import { EVENTS } from '../../analytics/events';
import Stripe from './Stripe';
import Braintree from './Braintree';
import { PaymentRequestButtonElement, Elements } from '@stripe/react-stripe-js';
import {
  selectName,
  selectFormValidity,
  selectSuccess,
  sendMainPurchaseRequest,
  selectShowPaymentError,
  selectID,
  braintreePurchase,
  selectIfCardFormShown,
  setShowPaymentError,
  setCardholderName,
  setIfCardFormShown,
} from './checkoutSlice';
import {
  selectToken,
  selectStripeAccountName,
  selectBtToken,
  selectUserUuid,
  selectPaymentProvider,
  selectThreeDS,
} from '../signup/signupSlice';
import {
  selectEventsData,
  setEventDataList,
  selectAnalyticsData,
} from '../events/eventsSlice';
import {
  selectPlan,
  selectLandingType,
  selectShowCheckout,
  selectDefaultPlanId,
  setShowCheckout,
  setPlan,
} from '../plans/plansSlice';
import { flexButton, fontSize } from '../../mixins';
import ErrorScreen from './ErrorScreen';
import MainHeadline from '../../UI/MainHeadline';
import UIButton from '../../UI/UIButton';
import { setCheckoutLoader } from '../loader/loaderSlice';
import { setAmplitudeUserProperties } from '../../analytics/amplitude';

const isStage = process.env.REACT_APP_ENV === 'stage';
const textAlign = navigator.language.startsWith('ar') ? 'right' : 'left';

const BackButton = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  width: 40px;
  display: flex;
  align-items: center;
  justify-items: center;
  height: 40px;
  outline: 0;
  border: unset;
  padding: 0;
  background: unset;
  cursor: pointer;
`;

const PaymentRequestContainer = styled.div`
  margin-bottom: 24px;

  div iframe {
    border-radius: 12px;
  }
`;

const CardIconsList = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
`;

const StoresWrapper = styled.div`
  background: #ffffff;
  box-shadow: 4px 6px 20px rgba(0, 0, 0, 0.12);
  border-radius: 16px;
  margin-top: 16px;
  padding: 19px;
`;

const Disclaimer = styled.div`
  ${fontSize(14)};
  line-height: 24px;
  color: #909090;
  text-align: ${textAlign};

  & > span {
    text-decoration: underline;
  }
`;

const StoreButtons = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
  position: relative;

  &:after {
    content: '';
    display: block;
    width: 2px;
    height: 44px;
    background-color: #e8e8e8;
    position: absolute;
    top: calc(50% - 22px);
    left: calc(50% - 1px);
  }
`;

const Store = styled.div`
  padding-top: 34px;
  background-image: url(${({ img }) => img});
  background-repeat: no-repeat;
  background-position: center top;
`;

const StoreRating = styled.div`
  ${fontSize(16)};
  line-height: 19px;
  color: #323232;
`;

const Stars = styled.div`
  width: 120px;
  height: 20px;
  background: url(${'../assets/store-stars.png'}) center / contain no-repeat;
  margin: 4px auto 12px;
`;

const StoreRatingsCount = styled.div`
  ${fontSize(16)};
  line-height: 19px;
  color: #323232;
  margin-top: 12px;
`;

const Separator = styled.div`
  margin: 24px 0;
  ${fontSize(14)};
  line-height: 24px;
  color: #6f6f6f;
  text-transform: uppercase;
`;

const PaypalButton = styled.div`
  ${flexButton('auto', '56px')};
  background: rgba(0, 157, 225, 0.13);
  border: 1px solid #009de1;
  border-radius: 12px;
  :hover {
    cursor: pointer;
  }
`;

export const STORES = [
  {
    name: 'Google Play',
    event: 'googlePlayTapped',
    icon: '../assets/google-play.svg',
    grade: 4.5,
    rating: '412K',
  },
  {
    name: 'App Store',
    event: 'appleStoreTapped',
    icon: '../assets/app-store.svg',
    grade: 4.6,
    rating: '36.9K',
  },
];

const CheckoutScreen = ({
  history,
  paymentInstances,
  paymentRefs,
  implemented,
  paymentCallback,
}) => {
  const {
    stripePromise,
    braintreeInstance,
    paypalInstance,
  } = paymentInstances || {};
  const {
    card,
    expiration,
    cvc,
    cardholder,
    stripeInstance,
    braintreeCardInstance,
    braintreeThreeDSInstance,
  } = paymentRefs;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const showError = useSelector(selectShowPaymentError);
  const isFormValid = useSelector(selectFormValidity);
  const eventsData = useSelector(selectEventsData);
  const isSuccess = useSelector(selectSuccess);
  const subscriptionId = useSelector(selectID);
  const [planDetails, setPlanDetails] = useState(null);
  const token = useSelector(selectToken);
  const stripeAccountName = useSelector(selectStripeAccountName);
  const name = useSelector(selectName);
  const plan = useSelector(selectPlan);
  const btToken = useSelector(selectBtToken);
  const [load, setLoad] = useState(false);
  const [paymentRequest, setPaymentRequest] = useState(null);
  const analyticsParams = useSelector(selectAnalyticsData);
  const landingType = useSelector(selectLandingType);
  const isShortDisclaimer = ['main'].includes(landingType);
  const userUuid = useSelector(selectUserUuid);
  const paymentProvider = useSelector(selectPaymentProvider);
  const useBraintree = paymentProvider === 'braintree';
  const showCheckout = useSelector(selectShowCheckout);
  const defaultPlanId = useSelector(selectDefaultPlanId);
  const cardFormVisited = useSelector(selectIfCardFormShown);
  const forceThreeDS = useSelector(selectThreeDS);
  const CARD_BRANDS = ['visa', 'mastercard', 'amex', 'discover'];

  const renderBrand = (card) => {
    if (!card || !CARD_BRANDS.includes(card)) return null;
    return (
      <img
        key={card}
        src={`../assets/card-brands/${card}.png`}
        alt={card}
        width={57}
        height={35}
      />
    );
  };

  useImperativeHandle(paymentCallback, () => ({
    onPurchaseClick
  }));

  useEffect(() => {
    if (plan) {
      setPlanDetails(PLANS[plan]);
    }
  }, [plan]);

  useLayoutEffect(() => {
    if (!useBraintree && stripeInstance.current && planDetails) {
      setLoad(true);
      setPaymentRequest(null);

      const label = `Familo ${t(planDetails?.description)} plan`;
      const amount = +((planDetails?.periodPrice * 100).toFixed());
      const pr = stripeInstance.current.paymentRequest({
        country: 'US',
        currency: 'usd',
        total: { label, amount },
        displayItems: [{ label, amount }],
        requestPayerName: true,
        requestPayerEmail: true,
      });

      pr.canMakePayment().then((result) => {
        if (result && (cardFormVisited || implemented) && eventsData) {
          setPaymentRequest(pr);
        }
      });

      pr.on('paymentmethod', async (e) => {
        const successPayment = () => e.complete('success');
        const failedPayment = () => e.complete('fail');

        stripePurchaseClick(e, successPayment, failedPayment);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeInstance, planDetails, cardFormVisited, useBraintree, eventsData]);

  const sendPurchase = (sub_id = subscriptionId, event_id = `${userUuid}_purchase`, price_with_discount) => {
    const { name, periodPrice, currency } = planDetails;
    const { gclid } = eventsData;

    sendPurchaseEvent( price_with_discount ?? periodPrice, currency, name, sub_id, event_id, gclid);
  };

  const onPayPalClick = () => {
    const localEventsData = { payment_method: 'paypal' };
    const extendedEventsData = {
      ...eventsData,
      ...localEventsData,
    };
    sendAnalyticsEvents(EVENTS.subBuyTapped, extendedEventsData);
    dispatch(setCheckoutLoader({ show: true, type: 'checkout' }));
    sendAnalyticsEvents(EVENTS.paymentAnimationScreen, extendedEventsData);

    paypalInstance.tokenize(
      {
        flow: 'vault',
        currency: 'USD',
      },
      function (tokenizeErr, payload) {
        if (tokenizeErr) {
          if (tokenizeErr.type !== 'CUSTOMER') {
            console.error('Error tokenizing:', tokenizeErr);
          }
          dispatch(setCheckoutLoader({ show: false, type: '' }));
          return;
        }

        const localEventsData = { payment_method: 'paypal' };
        const extendedEventsData = {
          ...eventsData,
          ...localEventsData,
        };
        setAmplitudeUserProperties({ 'Payments gateways': 'Braintree' });
        dispatch(setEventDataList(localEventsData));
        dispatch(
          braintreePurchase(
            payload.nonce,
            null,
            null,
            plan,
            token,
            analyticsParams,
            extendedEventsData,
            null,
            sendPurchase,
          )
        );
      }
    );
  };

  const onPurchaseClick = () => {
    if (useBraintree) {
      braintreePurchaseClick()
    } else {
      stripePurchaseClick(null)
    }
  };

  const stripePurchaseClick = (
    paymentMethod = null,
    successPayment = null,
    failedPayment = null
  ) => {
    const paymentMethodName = paymentMethod?.walletName?.toLowerCase() || 'card';
    const paymentCardBrandName = paymentMethod?.card?.brand || eventsData.cardBrand || 'unknown';
    const paymentMethodObj = paymentMethod
      ? {
          methodName: paymentMethodName,
          stripeNonce: paymentMethod.paymentMethod.id,
        }
      : null;
    const localEventsData = {
      payment_method: paymentMethodName,
      cardBrand: paymentCardBrandName,
      card_brand: paymentCardBrandName,
      stripe_account_name: stripeAccountName,
    };
    const extendedEventsData = {
      ...eventsData,
      ...localEventsData,
    };
    const onSuccess = (sub_id, event_id, price_with_discount) => {
      successPayment?.();
      sendPurchase(sub_id, event_id, price_with_discount);
    };
    setAmplitudeUserProperties({ 'Payments gateways': `Stripe [${stripeAccountName}]` });
    dispatch(setEventDataList({ 'Payments gateways': `Stripe [${stripeAccountName}]` }));
    dispatch(setEventDataList(localEventsData));
    sendAnalyticsEvents(EVENTS.subBuyTapped, extendedEventsData);
    dispatch(
      sendMainPurchaseRequest(
        paymentMethodObj,
        stripeInstance.current,
        card.current,
        name,
        plan,
        token,
        analyticsParams,
        extendedEventsData,
        onSuccess,
        failedPayment,
      )
    );
  };

  const braintreePurchaseClick = () => {
    const cardNumber = card.current.value;
    const cardExpiration = expiration.current.value;
    const cardCVV = cvc.current.value;

    const onError = (error) => {
      dispatch(setCheckoutLoader({ show: false, type: '' }));
      dispatch(setShowPaymentError({ show: true, text: error.message }));
    };

    const fullEventsData = {
      ...eventsData,
      payment_method: 'card',
    };
    sendAnalyticsEvents(EVENTS.subBuyTapped, fullEventsData);
    dispatch(setCheckoutLoader({ show: true, type: 'checkout' }));
    sendAnalyticsEvents(EVENTS.paymentAnimationScreen, fullEventsData);

    braintreeCardInstance.current.tokenize(
      {
        number: cardNumber,
        expirationDate: cardExpiration,
        cvv: cardCVV,
      },
      (error, payload) => {
        if (error) {
          console.error('Payment error', error);
          onError(error);
          return;
        }

        // console.log('tokenize payload: ', payload);

        const cardBrand = payload?.details?.cardType || eventsData.cardBrand || 'unknown';
        const localEventsData = {
          payment_method: 'card',
          cardBrand,
          card_brand: cardBrand,
        };
        const extendedEventsData = {
          ...eventsData,
          ...localEventsData,
        };
        setAmplitudeUserProperties({ 'Payments gateways': 'Braintree' });
        dispatch(setEventDataList(localEventsData));
        dispatch(
          braintreePurchase(
            payload.nonce,
            payload.binData,
            braintreeThreeDSInstance.current,
            plan,
            token,
            analyticsParams,
            extendedEventsData,
            forceThreeDS,
            sendPurchase,
          )
        );
      }
    );
  };

  useEffect(() => {
    if (showCheckout && !showError) {
      dispatch(setIfCardFormShown(true));
      sendAnalyticsEvents(EVENTS.onboardingCheckoutShown, eventsData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCheckout, showError, dispatch]);

  useEffect(() => {
    if (isSuccess) {
      history.push('/success');
    }
  }, [isSuccess, history]);

  const renderStoreItem = (i, index) => {
    return (
      <Store key={index} img={i.icon}>
        <StoreRating>{i.grade}</StoreRating>
        <Stars />
        <StoreRatingsCount>
          {i.rating} {t('ratings')}
        </StoreRatingsCount>
      </Store>
    );
  };

  const supportMail = 'support@familo.net';

  const onLinkClick = ({ target: { id } }) => {
    const isTerms = id === 'terms-link';
    const isSupport = id === 'support-link';
    const links = {
      'terms-link':
        'https://www.familo.net/en/assets/Terms_and_Conditions_Familonet_info.pdf',
      'support-link': `mailto:${supportMail}`,
    };

    if (!(isTerms || isSupport)) return;
    window.open(links[id]);
  };

  const onBackClick = () => {
    sendAnalyticsEvents(EVENTS.subBuyClosed, eventsData);
    dispatch(setShowCheckout(false));
    dispatch(setCardholderName(''));
    dispatch(setPlan(defaultPlanId));
    clearFields();
  };

  const clearFields = () => {
    if (!(card?.current && expiration?.current && cvc?.current && cardholder?.current)) return;
    cardholder.current.value = '';
    if (!useBraintree) {
      const fieldsToClear = [card.current, expiration.current, cvc.current];
      fieldsToClear.forEach(field => field.clear());
    } else if (braintreeCardInstance.current) {
      ['number', 'expirationDate', 'cvv'].forEach(field => braintreeCardInstance.current.clear(field));
    }
  };

  return (
    <>
      <MainHeadline customMargin={'0 0 24px 0'}>
        {!implemented && (
          <BackButton type="button" id={'come-back-button'} onClick={onBackClick}>
            <svg
              width="11"
              height="18"
              viewBox="0 0 11 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0 8.90039C0 9.15674 0.0922852 9.38232 0.287109 9.57715L8.41846 17.5239C8.59277 17.7085 8.81836 17.8008 9.08496 17.8008C9.61816 17.8008 10.0283 17.4009 10.0283 16.8677C10.0283 16.6011 9.91553 16.3755 9.75146 16.2012L2.28662 8.90039L9.75146 1.59961C9.91553 1.42529 10.0283 1.18945 10.0283 0.933105C10.0283 0.399902 9.61816 0 9.08496 0C8.81836 0 8.59277 0.0922852 8.41846 0.266602L0.287109 8.22363C0.0922852 8.4082 0 8.64404 0 8.90039Z"
                fill="#323232"
              />
            </svg>
          </BackButton>
        )}
        {t('checkout')}
      </MainHeadline>
      {useBraintree
        ? <Braintree
          btToken={btToken}
          braintreeInstance={braintreeInstance}
          braintreeCardInstance={braintreeCardInstance}
          braintreeThreeDSInstance={braintreeThreeDSInstance}
          card={card}
          expiration={expiration}
          cvc={cvc}
          cardholder={cardholder}
          renderBrand={renderBrand}
          planDetails={planDetails}
        />
        : <>
          {paymentRequest && (
            <Elements stripe={stripePromise}>
              <PaymentRequestContainer>
                <PaymentRequestButtonElement
                  options={{
                    paymentRequest,
                    id: 'apple-google-pay-button',
                    style: {
                      paymentRequestButton: {
                        height: '56px',
                        theme: 'dark',
                        type: 'default',
                      },
                    },
                  }}
                />
              </PaymentRequestContainer>
            </Elements>
          )}
          {load && (
            <Stripe
              card={card.current}
              expiration={expiration.current}
              cvc={cvc.current}
              cardholder={cardholder}
              renderBrand={renderBrand}
            />
          )}
        </>
      }
      <CardIconsList>{CARD_BRANDS.map(renderBrand)}</CardIconsList>
      <UIButton customId={`${useBraintree ? 'braintree' : 'stripe'}-continue-button`} onClick={onPurchaseClick} disabled={!isFormValid} />
      {paypalInstance && (
        <>
          <Separator>{t('or')}</Separator>
          <PaypalButton id="paypal-pay-button" onClick={onPayPalClick}>
            <svg
              width="85"
              height="34"
              viewBox="0 0 85 34"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              xmlnsXlink="http://www.w3.org/1999/xlink"
            >
              <rect
                y="0.422485"
                width="84.55"
                height="33"
                fill="url(#pattern0)"
              />
              <defs>
                <pattern
                  id="pattern0"
                  patternContentUnits="objectBoundingBox"
                  width="1"
                  height="1"
                >
                  <use
                    xlinkHref="#image0_2536_1488"
                    transform="matrix(0.0025 0 0 0.00678363 0 -0.902534)"
                  />
                </pattern>
                <image
                  id="image0_2536_1488"
                  width="400"
                  height="400"
                  xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAZAAAAGQCAYAAACAvzbMAAAABGdBTUEAALGOfPtRkwAAQABJREFUeAHtnQm8JUV59rvPnYVhmRkWIcAMzDCDLGJcQQZli1uMMeiH+UyiomDcY8yiQQGjUTaj0agxJi5DjPhFE1Fj4pe4xAUMIxp3RcCBGZgBlEVmhmWWe+/pPE9Vvef0ubdPd/VZ7j135qnf7dt9uqveqvrX8lZVV1UniYwIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiIAIiECvBNJeHca6W3ryxWNJSpNksW4GZS9rwkt4zD+TmTEUGf6naYbn2dbrLprxcFlYdBYBERCBuUygVbEOOhJLnnBxmjbSdMu6C5uDlj1IeQxnYywdc4olSZqjHt5Bxl2yREAERKAfAkNRIKyUGSgqEDT3n4rL09EJONwFlH2RmTDoZaBrMQGvcGQP4rwNIdrqz9lmXP8cFm5Db2RLvheydM0lDHWjOZlN5u/PRJDlhwiIgAjMJQIDr8ypPFjxLjn5kgMaafZJwHgKDg4TDdyvPkEzTPfiuAXjWT/G+TocX8ew1k2mODj8BiXTtN94LiMCIiACIhAIDLxSZwuew0A4/xNee/wO/BnHMXB/+khBhoVHo0AGwpr+DzTLP0BxfGrrNy/8JeND+4jTZIF93RIBERCBPZbAQCv2tvK49Pg0af4AVMcC2YH6M8DUYi+E72ish8Tw2vU96Jm8E8d7tq67YAfiNiYlMkDyEiUCIjDnCRS1wnuOFN8deMfZiTjPw8HKeVSVB4PKsFFpMKymPBhm9jYORA/q8kaS/XDpmovPoPKAEqE9GREQAREQARAYqALhW+tgTgjn9h17MtpnUyimTPgC/mjc/AqUxx9DiUzsf8olfCYjAiIgAns8gYEqEPRATGE8PJAd5d5HVeKTjfWimojau9ATect916InwrUtMiIgAiKwhxMYmALh7CurXKE1Vgeuc1mBWNYgIx4TiMyb0RN59ZZvXiQlYnR0FgER2GMJDEyBtAmmh2DYx6/5GO33H+0gV19REbpeB3oifwUl8linRPwMrWrXsiECIiACuyGBgSkQLBr0stKEymNJYLU79EAs2RkXvlxfiIt38ybeiYz0KnuGUUYEREAEhkVgYAoEATRlcVQI7O64boK9EMQrOw29kOcxnjjrfUhIcJ1EQAT2LAIDUyC5DUrs/Ye9UN8tiUJbvooRw8p19UJ2yxRWpERABKoIDEyBwCOnMFCxHlfl6Rx/HnocrhdyIrc5QS9kkBznOB4FXwREYE8hMLCKD5sPWkv8qADPhrR2R5bcpBEme6Y/D3Y9TZCpkwiIgAiMNIGBKBDbQHHxEy5ejErVFMhAZI8oPaccscPwmhA+U54jGlwFSwREQAQGT2Aglbzfth2LJRrpkQjiwYMP5shJtN7VsYtPuWwRZ2PZFvYjF1IFSAREQASGRGAgCgRhswp1Vbhmi9zuDSnosyrW4nZAo9ncnyExJTqroZLnIiACIjCDBAatQI4JYd/dh3RMgewLNfkrM5he8koEREAERobAQHaXLdgDa2QiOMSAQElmUMCNvZ0fWWZKZYheDlc0h+FaC0KH65Wbsadv0g8X8khIX7uJ5cKOYQbJlg1kyXnL7XqY/s0t2e10iAl3NMOBKBCbgYVcsjKEjhlmdzchjplfMJlbCDMXI86pyKzQuW39TIY/TIFmT3jG/Z7JeO6RfvlKK0GFPrMjEt5fTrenImnu8Qpl7aZGSIM4xUp+PCIUcd8KxGZgLVlz6V5IL74DodndFQgTAnFMWdn+khGGiUscb3ek/jMN3USAky952NI1l9ow5JDC6BTuTgjf2kwbd2y59o3bce0qGOsBcXGmPiM8JPwzJdYqoCvu2CtZu/kRKB6Lhug1ymG6CyXyQZTCO5MHd2xLXrM6TLWHr2s3UZnsuYqECvzKuw5Odu08KEnTcmWecVQl3ZSct+z+GCXStwIJL4+zNMselqQ4vBmcAmHLnh8aaS0zCT7MxImjUg1GBQdPZlofPskewMjV3bzN1rs9nktn9gCoPLBV/dOxUeQ/ISacFBAU5DBiQpAO1faxrHn3/msuuQG/rsbNLyKdv8seEMOEwym1YYRAModMwJSHr7Q+h3R9Qkf5GYr3yEXNbAIV5L3JPgs3ovJbh+svI6td3aoMqUjOWz6jPeyhRDVWqMV37aazk507PoZyNx88yso2n81HZfcdKP0zW9xKeiJ9KxBkDl+1pq73wVYGNRyHJAZiGjsfShrjO6CbZj7d8TlbsPRRyRpjSTYG/o15STZvPh7xzU92b9ZosAU9J431PLB+ZwHWtPx1UB4EzUgzMw3BOLHMM8gr2RE4H4EfT8P5bcD9PSiyd+H6E06R4Lsr3PUYv2XmFgHmn0m0eN+EltUTcD2B3MRewDAN8xQKZ3YI8hWO5CQU3j/C+Y5k7W0fRjl+T3Lu4b/cw3oj1tt4LZiwbmZZKqvzfZlPkxNQFg+C3ftxkGvXuqD/ip4VqTPpcf7shyPCde8niB3bvi0Z23E/BorGEQXEYYaPFL0e+s2DSoxhmffQfcm8B+7NeA1zzzYMwbhhvJe+oH+WvdPqySUQuzBj/c7JgHsshJjyYJoO82B4mSmZwemnDTc8Bp5eCc18LXogjwpb5pdleDiVGSkCvvcxmVxx+wIk8a+HsDGfDTM/UTYN8xQP5qlJ1BdNHIfi+PMkm/xp8qGNLww9kIYbnoGl3dZYL/CK2zHJxzXUGFVyMkZF56Bw0o2wtTmwob2upu9Kz9QHfDi+qy+1HyCeExNJYwJD5fQg50ltUf06MP9zZ1RwY2Pb7kvGjzj80ckn7v3s/S/87eOS318x6Vo3TLi5YlrKP3l0CDIzy0yFn/4w/7FlSiXB36ZQTgLjdeiNPB89kQkok2G3XuG1zIAIhPyDHSmybEWQOZN5in5ZnmL+Yp5GDyg7CMPR/5hcsfn94R7fjcxUuODljBtLB/bGbKkB75UdIZDZbRi+GgcfvnwfrgJBp8BpLYRqVfA9BDz86uUECY3JXUj60rD3InkwboJCmzzwgLFkcvKs5qJF/wPYr2i1bgbjy9ClIBoGmL2PUTAs+DzYguR3V66E8nieDWeNQgAVhkoCvvx75cGGAeuH/uuESm+7WqASsV7seNKc5C7aHwwV42yGq2uAB/TA0mEl6tGFkBmTDqE+SG8MYajsYFRaKItMawz91LfjxUuyMtgdSKLMxjuPsrh2PINiy+YvSCYfduAYe0rQdAsw1vsBKJHLnBL58EZWgiNvGA0GEgk2OOU/mFi3+EHHfRRK5ER9AXIwYGdAipX/hwe/XANzBvyt8sIUyTjK6ktQVv8MZbWJcyuvVQmYY88tHXp5tXBDbFz7UiC2fUdjfAKzrzDW6I0FPDYMxfaao5LvpgYP0ZuYTJqL9knGD0a0J6FAsoyZEBkze4PLmDacNdXpCP025Y/Kma2zI0LQBpN2g4kn86b1RN6DcLrZYgz3YMRLylAJpOmQp4P3FHrmHd8bybK3oKw+yjX4OFSzu5o0rfNqwTj8NOCwEYqudMxBVwsVD3xhbn/Glh72X8DRNE6brDtG0CB2DNv4ww5uZvvuh44hFJ0f0kLGdC/tLkfGPD1kzNFv3WRu88tlgXT/aTfYJCM/ZIRsDY7fpejGWJgWN1h/JG1wBHzLL8tWBpGjlqcYHrb6OCvpDYOL9shJCunQPDqErCodWHc3UJftxLEhuBmuAkG9aYE6KnjoAx1+9HaCSNTDToH4irk3McNyxTBNQoEcsayRzKPOaHlEFvjlpjW/zd3lnPMRfVHX2rLEK/99fdhb6dmKVMkFY8705tmu+Tt/8P5ADLLaSyjIdj0YiFAJGSwBP/OHw0Js5VudYHVErF+Wn4aZp6xh90y8VF8ZhrL6bUzHxm/49iwd3Ey4ZGXwsCodrKz+HC3iXwQ3dq9rmPuC1n7Hndk4W6WHXUNiDxDNFBU0lchIGvQ4+P5jfMWRbqbYlCrXt5iz7FQUorNC+PtiPEQGIUNlGKt2yVYXON0zbjzbNX/nD95nV7Kf7mTglz0Rw1jH6AuQoDnqZsHCAxDEw0IwmQfqGMtPZXmKGda9fKwjOGeXsjEzq7kfhp1/I9wP+Sxna+5eeuZZEzOwUs7CovH3/HXR/1B3p7dhvcxDruFbMQOLQvx4YJG4iHtojDtP0TrkVgV0URXICKkQ0mTeGEGDCKe7diXjv3IoDsyMm8D6FPZIphmySH8H//4VTeZpT0frRgMzsJzuCBkoOnQ7Ecd7ke4BQJp3zwRk94yr2jlUQENPaLcIGJ93M7RPiFhXkJyM40YcLOx1FR6cyAyZANOFCwhXoWLG+K6rFOqkN4Ye3M4ORYXG0pu95aWQz7qLeY73e6n8LVxnwH17ai9+7AYmxC09FHhi08HK780h/j4tK2D0rED4MvO+a/3Ge/B5tYW4wr+oxyM7A4vKAr2jHQ/H+8G9UC8+9BCyruXDVtRCZs5Ow3YA+8VsB9ByOYMXbeXvFhDS52kR6RIcpxzwluqj2L7lFSjwXLE6kWXtrQKwvISZEXkrXQJ/ToDg16CsPw33TIl0Ed31dsjc2aNoo1Bnd3WqBzNIwOehycnVoVwwvW24qCwYXgmk2INpwV4nJbt2cRweWz+08xQc0w5zKXsNR+L57+GMYU33LoP5Izb/OjFt+8j/fr2DH26OaHWbgBE+BxbZ0b5d79jFpAOjZC/Qo3j2rEAM3hJswIeOyLLwO8pTc9v1PKozsKA8mov2TnYeAwXC6bvFseVdTJDNDsP+Kyfgeh2OKG3elceAHwxI+f8kDCfdx8V+XYJ4H/y6dct1F/07hp/+EPnkPbDXc2EHWBsWCQqli6+6PbsExsaOD0PQselk9m5KXnDwXa5CP3eZVxjTY8JN/n6O9xbr0ED7GPTKZ1xZq5+vrPRiuC1lK33rdK/m+p3W8LTxLYuQ8bAeSIyb3oewWpsopq5QsxU6GDOqM7AamKCAHseORz66OXnIIQ1sToZ8Z8ynRd1aXpxCRwXS1eI0lzN4o0flb8MF11tQu02tDXmksfjUt6dbrjn/vdjpF42N5kVwxyGK2BaReYNzupg/bP1K7oEuR4OAVTqrQ3Bi831wl/7Eu0PvY+0mk1UUs9RtlXLu4d+Cvd+Dha8iV9Avuon10+RiSCxjvqICMRn2bK6ejecjPZJKJrRv5XF9iHSQUY6gnx6ISyj8s8zSY6UwJYB8ec6je+U8xcEM/Qwvz7c//rENN3W33FuDby+wym3P8NOc8seLHLdpWmwIGC8qEPY4NgVHZVuv0z52+r3EKR0Mc70Lw1vnIYHZk6CSNWUURFWeLJNXWpSFGSbgZ/6wDqCxOqFuZR4qLzQRyoeS6A+GnDbPxxDx16FEPolWBd85WsONYYgzGXafrRvKOMmzY8vS4X3r0eGv+XkN9/4p3RgCbnVYaTz6ViDwxd5/RHlYGhoqjUmMro/aDKzQ+9h+7AnN8SOOrOp95KPI2SijaKzIrAqBq1mZp3c1m9mddBuzjT2GuJr7n3LJGN6Z3QdlcjU8Z2HvJb88GMLb84m9paBAKcM48Bpb72WZvkNCFH2YK24/ABsXHh4k5PmWCUVDwlm9OViKzButiuLf4Y55qhezPVmwEBNCnIn0d4o3rLS94dmuzRJlVilEszu48z57HYTCuSwInBqmqf4ExZveAoW8NaHyKVfgLfc9KxDU9QYb00AHZ5zy4PzgUeqBsPeBl+YPnnFaTO8jD2Nh+GGs8s9m7RpoLUOtDIGIVSAho2Wbt1130Ta6rVHhBj9TtDIdjjpMzK7bAhneWvhD8LufgsJoMM7IVk0qM9g2edMc0j4WK45xmAwKpWvvqtuw3VSBVXxi5VBulaypftf93WdYmCbg6nqX2KIh2jAtoED4LY/GxuCqa/oUS01/5rMEP4YUbegHS8L9yfj4A9GuzKJfvU7/qBys59U93H7LFDKaLK2c28rIfJp+Lq/cLR2WI25Q5s55VXmxcG9wtvdZyHhZnKb7n7vTswJhAaMchKzX7mouGO3LkZvCG3of95/55GTyUMyKK5551Y5A55VlzKoE7HQ1c79slWpNH1lgkyT0KqIyWk0PCq2jb3obH+QUYKE93uSwGew10OvhcFsrjPxyJjTJAci4HL4zBU8nDyFP3wG1cU9wk7BChZwxbuZIC2YoOygiu9X1TLvdejZlz4oEkjfLXazfRTKK7jGe6JUxTh3xLLJr9woYWB4/MlRalBUz5Mh6BBV5ehfc3R7kW4Vm3lWf6Xt9V3CU3ufWPVT7YLv3Mk5UAmyI8OB93EuZpw5B/uEL+WDwlcQkuwvP8NIfu9ua8cqk8wuJXiHF9VS8kmEvwftvcv3Z0sFmYMWmA1xnNwVRJqNTcsGvnhQIMxwz8eKTL9kHMlcEudGeFoSjdct9+6P1a5YvqDx27kzGD1+WPHTyE5Jkx3a0lWpF875ZjkGh96iEXMZDTLB+x5nYSLkiijrMjVVDTqw7ehKKd3aw97LW/+BPtpGu4G9Xw4qND0Ml28THshYjGdfg1umopR4H18eiL8IWsq1PoXWaJhTO/VmaboCMb+H3v2GY7otb1124C787lAhlx7bWQzicB1P/8Rlkz0M5yiuyqdbcb7w7mrzv2gt2xPpbKKTgpslDWCYXn3LZorTZJBcSJnO8tuqYSuslICxQipjD3mF8GmWZrQkrSaUOd8Fe+IwqH5W3sDsc+x/umx+8tHAX2Jl2y8K3wT1hpd7uSXRa9hU2tzZnZTwBhTEP718wpTx7Mn6fiAOTZZqH4YuIfBmP/GfFgsUs3Y78djvcfB8//hO/Pwdlcjd+85sk7biaMuDwUZXxfCz8U22b+1Ueh5W7qdY6fns3k01XrjueVPzoSYHYS1jUpcsQSL4QpbGA+1+9/AeSkdkDC7UJ13xw1fnWZ/0WVjXMx3aJaFDwfrUxS/dWW51ZGy3lj8oCY6Qrgu8W3qrABHupnyuO2qXKgT2HTWcXI59H1fST1p1SwNnP/Crwl/Fij4MVIR2gYsYMlORl8O9snNF1pP+26BG/vMmHn34sgX1+G4XrTV6GD219H3JeD5lfZuuf26hwGAn33gEQZ8Hxg7CP1ud0A906CTt74ZO935psZi/FkN8uhjG4dz0YyDkNdj4wlnLBZcdCzOkCEXzIug4cX4uHm0xWgcXoWyaDDUEXp2zy2SjFxtrLwbQ5XNg9XDOc2SIo2k/g/qtangFOuLYRidajigtz51u/ZRX5dEEhP7a2TcmHdbrtzjvm743htsnqtNXuLUyG74o/H2r1RWDwqwX5iW4h10S7nyhnHKVxL7SfC378KuLFePJeZ5fK6LzlVEpn4felkM2eSnFYKBjf2YNdfGUvfWly7rKf4JqKjfE2Y57X+UQD0hdejjVMgZgMk9n13JMCQSRDBLMVkEwZdRKua2Dc7KbWe7Hu1ob+JCiJFGs9tjzzrAkMXc2rOXRlGWBDCGt0ggw7bi3ln6HF5D/9SS8tvFXeo7J0Vt0QFjJxVLxYUbG1zd4APLBej1VKVX7SD3q6FZWn9XzyBcYNVzFPhi3fj4HlyxA3aP3WEArt86CcqQduOWNx4dnl56BMvoTK9Q0Y1no7beEa8jN+KnUeBFnY+KjDwA5/89+xjUbjQzh/gwoO50mywBkByd6BE1qv9K9y/J6K8TnQkktx/jUcfZuwMeUkGoIfRlj4EjpiuMM1GhCV7MVLT77ksi3fvHATmDS2NMZcnCDDKi5yrmNuCJbruHOQ4e6kHtyaP+wZ0Jgs/yvf6+CC4LW3vS7ZteMPkc/I3+xOeMsuXU2encMjZ5f2yQfP4B6zEfG+56k4npsbPjsfDTrmhfI0yDgDkqvwGy+A3TfiYJ4y9rhshW0Ff8BMDY+/2/7PsPFd0DaU51vDbYtf21aXq9hC3OmcbT1nWls25yPQaTf6F9liJwMqEBMf7XaAFoPf6fbtyf2nnZHsfNxj6ioPwidXJnTvY7oDjFKHqJbyT1biPodOQsbusFX0I2Sq7B5UIRtogWP7RRan3gNSl8/Qoj8Nz9gbiPWTomgXJr1+6zcvvNtazf6eq9A5vITRpmQM60zehortewjZc+gAB9LAuaf/bOiwt8DrkH9x1Ta8x8Ps8szCPAGZl6OSfCWuaY7DQVlsKTL+DF/RQbch7NlOXBcZyqEpcp+/R38YNsjMTkNYHmk9GTruxUDGPCjFSZzZS6PysJYv/So7GC+anyJEd/Bi+xqM4nBYZfrnU/m4ylha/CRYpN/VhhU8h5Q46wtMggOmWYyhH2OoZ5gu3wkO2v7aC3LKdz2D5vWo9PFZ3GwJ7FqeYriZfjwsT1lccKtleM/ylPVW+U2SZ+D4qLPl47Cq5aI8P1g4MZ4+xXgmzZAOJq8oTHmHQV56J+J3V3hgfuTtFV7HAu9wjArBPLDWRsfznn4gmq0ZWD0JGIAjKg/UiU554KX5Q6chX27HUG+99x6eTZrenqTzbg2hMl4DCGSfIlrKP7EX6LFhM3t3oiK/r04o0Otx1vHfhjxMVowYZxf//puWOUPKHKHyc+8msCByxVgj/VpYpLgAz1nJ0VMr3Oak7pl+uTKCLH8Z3xHg9zFBCOXzWdlB9+w5bcSZWYtKgZMPnOLIksb/5+9gyuSYX2RBmWfm3ITL+BO4cQiNnwpmb+ovg0tjRb/KDscfAbkAMqiAxnaeQJ0K43q1HZ9P9ffL/7Mip42NwZpL73BddnLhgJ+/C7AcRrc0L3Njz8wP9qRvCTf9vdaQED4bsHbT+yD/s5B/OOxQcdCQk/fb/az9j+mM8XDIy5rPxW7Az8SZ0/0PDpIouywv8BnNjf7U8d+BREIcgTAfEp6Eex328j+MBabwuqE0vguye3l7hdcWmMKH3W6iIDgPELJVwU5VILuJ6rg/qzOw8KaVH4pKx8eTbU/7jUmnPMpXm3eEPfcjwE+vT849bEcYo4xOkJycoVz6skrR2CfHm9iwOXtQrxvojJVQzLRSDFstCC3dZ8PPZ8Ap5dQpgCGPZl+mv9brYcUVKrA1jTT7Jp6swWMWctqn/IHkySCPldMSTKd6K86n4KCpkh+4oiGBmV3eif9vigRh/kwQ4xRK3k6Xa+cnKv1n8TlYWG+gi/Xi22TInhyEfQA22KqOrXzJF+OW6Tu3rLvoC3wvxDTgPRw0K5C69Xu1WfJLDOds8iJ83RKui0/23uDKu1Dp4iNu3oR8Uuxkyl2nyBHsr6Gy9F8lZKVpymPtZjBpfh6A/wDuGGemJdPI4onLvo0Pb9Z8HSRxXy8KDOEqlR0Ubro+2Mq7sfCxXmYZ4DO7F6xPO4V8Gt4vVtvvEBCbcVuObDwbBZhuV4YHVYFsuS+7mJVNFFmMcHCbksl9lyT3P/MZyc7jHzHmeh58Vt9YgvwgOGVGySdyfYmDdRHClyKTucvYSAZ32c0MDtA0kBcsrh0hDO9ZXAFBBcNZTI9EpfehDktxP8iNcm5pZuk36ISV79KTLw7K4+IzEPh/w21sR+EKeu38TJkRhoURL/6yP8XZePFemXFs8G+9DTeBhcsHpgRx/h56t9+HwEdDkMW1TKavdJL0ZPa60BPcSEVucssc2jOWW9hn7+N8ROlM3GcFGcONioL2vgUF8iac899mMSarQ54qzBd0M8XQHsffMX262e7V+vcPU6w67vSHQ1cTbiuTXTv/ARliGe4xbFXpkZdndqHAg+lQHtl/QC4bJBzWY29hGManZZadDmZnBA9C+nb1zvOiwm2kG4It3jPj0yHjHljOxOQpuHHOfmpC6pxjMk6hPBTk/VGJHBoe+oAX2oy/mXJ79JkyVA48xrF+CbOrdqw6pnn/b/5Gs7l0ad13HlNDjEzgZH8jPMgn8FS7M/qbyp+9Ad/6rP3FuJDGfr8iNphKeiBs4bpV3aiofhMV74cR0YNwxGToPBNnHxXWp7d988IHIcsVfFSAHDqhUvo0Ch+Vh1VuebeDvmb8GR6awML/6PI/pHtb4cKecx8USqjIL/5X3I9VIPQXcc32xUjk6bjeiIOVjoULl90N+QXlcSLYvTXYtMq0u0N4iIe0tx1p8cqt6y7YAVlFiuuYICTEvUykexbsYV1RvgdQ7Iw9BL9Ceu2m1VA4a3GcCqtM+5g4mFSXp5CEP8SNr4Wbfk0Gt0ZJmv8yA8rDwmLnECb72fVMXlC4WFeSV7ht68Y9jCm2H5RcYa2Ce7o+2DEZJU7aj2orELQumWEnUfeytcHC6yPVltnble/C9eY21pUpDfjF73q4LwseeGjzwSeuae581CPJopHs2IH/MfVDoadkwQThoqhvBRtRhbtQ2rBuui3YMU7qTWxkme40t/IfXlovxKK86fkny+YDM8dzH49ppy9C9rBhq9hCQvE0ZEn5bFVciQN3soQzrdD6PgCB/mfcwfTX2hWIk4R/DI/lXTLgtf1mXIu4GAM8rjTBfepmFxVk75AvUrSCszdBGuNq/lcKh4Vfx/FRsKabSsNGAxWvW0iZNP8ODuxdUUzly7COodRcCBnfhfJwyq/laXsK71HhXhG7lvXchYX9Fn8v3Se54o6CViRnqLnZS9x6nRMkXoQKdD+c6yoPesO4oIym/xCGr4w7ZGXvRx57Kp732vOgbB40llcYRx78bfdw2TJkVZfX+i4KN/jdGp6ukstw0Q63z78thMjSJPwsPxFeXWOByneTYjJhiT8QieHcgX/GlgqDhmeWYPeOA4pjbCwZX3pwc/vjHpPtePQjx7JFe/v9rWi3d+VB10xAsEi/EhYLdV+cRNszbICBGZjKfwXOGOetVWE5mPj3USiGCbgNzv1V63/KijBjb4MVFM3UAuXvVv93LFFpXYVK6wds8ZoTJNF74AcncDAcdfIwCwflUlZVnmXlRHsu3jjXNSG8YXXvlLUriJPjwrhxfQfiwyETK9Blfjm50BunQ5Hujx4lt8x360vKHKHhx/hyRtnbcH4sr3HEsAuVdPofCOu7Q88jpD8k2GwoXCL4R7lTPLPgf3YOpslijQSQdJ/Gvz/oYBq4q98sHavSMASndaK7eagPNiOIV7bu+tlWWN/RfCnuMW51h63IiPmEaRPSHVfTTd7e9KfVd1zkYe1nwSr9cvmo3Ttzvahl4XlV3qU82vk5Tr8IbsyP8LP8FJOBukk4oRcPC4UhCikqd5c5+DK7oLlW6K7bTX5PBE1kp5CwGNAZKI3JfRYnu449vrnz+GMbO1eubCT4tofblr2/XseUULg0+9SUm6PxszUDq2OMtGYhbC0crYpTAF9ZURfJYSZmuLbjuJQWmvPmjW275vzxpWsufj4yyAtwi/Lr5F9f0Fpy029jLsh/I+fdCDn3ItUWwtMjcMZLcrfCmL0bc+MSFb9jjRXM7Zg1vdE5Klgzw9lYUAATsPx5eEAFQv/KKiCKYlho71A0BE7H+bPWMMB1oQnvjPDe4+KnQ4HwpS3DF8OO9pgOv0BxehXO3Q1faO/aEVtxdcrJ3M4AnfeKfzHePBimKk5FEkKeSd/hG3huN99xVL7LgeRdwUGd8kA+ITycW4CJMylezGfZj3D/bhxURofi3iOhnJ6G+6vx29z0En7Lh0XvK/gMYUgehrd1h3hvXF7B7a6GYaHZUOcztt6J/x+TifL2wcI3ARDaVeGBRarDXk8/JrEOhJU5KvteTdaA23ljSRMryJtL9ksm9lvanDj0kMbEYYcl4weD7eIlPuHw0bPW4sD+eh0WVJ+RUg7xpF8MN3lvZAzSzszR4cIykN2POdNNlTv61HsiBuUAT/4ard4fuSETKA+umIbgN0cWjnxcWHEwPBzD/wjOlHuzb7lf2IqLG+a57qJ3uyGyNHsd/OJLc/akmI51CjxlksGdKDBo3SHEBWtm0E6y/PE5WHkLDpZHc4vLrsbCw+HBz7JQdjMuTn7Yb3/Yel9gF+MHRdIfDF0lfxpe2HcOXXlPyQWfsd21AhWkLbLrHiDvZup/hodHmaFM+lUnHfLyTHl8BzO+OIQH0+ruvAlh57ArK/zYOjGkHXp2afIlJPclcHsNVodbmuJnznBFe5qil5W9E35R0VqezFmqvLS4rw8288w88wy9QL8fl+WRMqHBfXpzsOTTsszFlGexsJwzZsYwJRMeOW3K+3Uzy5Qg+J+N8e1Jc+Gi5KEnnZqwsu/VZAvmQ85e+HLgXkm2cCE6owsayTxEk70SbkXCqbk0LHSDURxeXquSSdeidcOvpo3U8FUIpGUYvOx0l72kHd304s44VZ2tEGO2T+OtectIrj9AuKn8zE7+cbdrs0t5525Zd8H1zMc8uKYEygkiQ5cXPTT8ZsHbgmmqF+D6P9Feugq/OSRHYLHxDpyTW0peOLv9ulwFDyWJYaxr4cVp8IP+VylfV5EgMGdymjSUYWubFLjtMLbaHNn9nTXZOW5QuGsh/+OcssveUodw/yMwaY27x4R/qhjKiGU71W3Mb6YHmHJfqvRlaG3vQvm0LUQeBT3y4iCkirv55Sv/NN0Cma/A+4hPuiEkPvVbnzAulgfsmi/qYW/zVzA08s/Ic2fAjpeDiwgT8h/igFmJwb75wZ/Gz8p2/lmw3vVkizhNRleLUx/UUiDmGMXtQGTIZeF3bU9NTuvM8ouhq61nPycZX706SXaNtx7VvqCioJlEXqdcKg2+MGcoqTR4DN4wsTi2eje+LRBaN63W5eB961EilT+dolI8KogYCoweg0dnDB/z5C9QcZ0TKl83c8j3PrKX0RKMq0D9Zel/k/dJdG7P2XbdBZxSTPm2rXtRhejWuOArivPxFcWrYf9s8EIL0/VEmM4xzGgPNYh/gY4sx/CGjMknbRMWRnIY6yoIPg1PnNu2jcIryoO97Gis7n8crtfZ5Ja8bYSdlT5nrD0PcTgPz4xH3lrRNcMKTunPUIReTwvcB6zIYvtea1g0JvxtZ8O/YngQdvYU0tegEv+uUx5tzq/B8/k4yCZGgZADZa1HT+ZZ6HHcEJQGV+NTBo/phu+KnNLiRoqbfwuraa6G4oqdfUd5jAdnYN2G8528AVPE+jj/qPBZeNQ6WV62IbEieS3LRRcsTNEmzO/HPmruM7YcI6axQPhfdf9DGIetHnrcY5PxFSuTdOtW7ArE8jEAA9kA3m8IYwISCmb6N+GbziPX+2BLl9NH/X5UKZS/yyv9pV0MmXg7rMxZaT2Aive56CncyLF7c47Fgs/G9VE4Ygt6sJd+Bvuc/C4jygoVrelCpWH+8Aw7rCSabSVy8WVw/2bew9EKE66rzA1VFlBBUybNF3CwdbkIBxOnKm1CnnOLCtdNtY+4cprtJParWg7l8W48p4ktWPSbza9XYejql4Eb/SsyLiMhuA8fwTzl0hGBZuPuQlTwHwmVvf8mxxWbD0MlfnaIVAwbyuMMrluhPJ4G5bEhfBWxusXrV3f7nXz9CAXeKaX/DWb0Nya9A2f0PrgO5sMbp9Yxlo/YQ6epyj+Ux7yM8pBtoAMY88P/ivgfA60lhnU9f+Df6nCzW6Zquam8QDbFUFO28xj0vLB5oVMerYofPvVzXen5QCww4ZhBf4xMhWECZywxw8/ZPwXlj45eeihCg5dsI2PIKigP9jySX4Py+IZ78YuxezxzmRpZ75waIaZMFI70BuyE+2IqTsafFWoNGZjqjUVCMHgR/l6c2OpjgYspZKFc+W3vy9xQWYVhrBthDxWKMzH5x/mBEvJ0uoCcCcrxztv/UXwwY82t12Lcpz1v22xdMc58I/xXkPllKA++9+jOzbe66XhlS8LsXzCNXDwQFZTNxp+g0r3UKQ8fXt8IyLLnInH53iaWTajs0/NqKY88D79dCIfP1qHO4PAoTUx6W7672blA19Wd+c/PhPMr6tt1c/t5y2LHhZfH77CkY5vCE/Ojw2LZj1oKBHW9CxR8MQVS28OOwFA5YJbU5IFLssn9lzYTLiTkvTlp0teHmQxsGfTHZQjxB1YDuxx1IAsQM63dG4KPlSLpv1VM7Hl8FRX1iaisvu1avFAeoRXNb2asgt1TgsSYPOvihabJH/HLiaESjCmkHYGm4uHYP1vhCN9/hYcW5g67uR9Me4YRFVi6kffZRuK5m4FycxUaite/dLNTcD+kXfoYbCJ5PJ9bI8Hii/Mr4ftz8Ihhdn7QXomhPaRF8i302i6kPbz87x5fVlw0fhPF5e4awQjn2Tgxjak4yJuNOuxHlz4V5fHdOeXBcIU4OTb8HWO8m7TxHsj7CuRBCeQ+EhUjoW3H8gNma9bG1b1Xm7rZV4cFb6oEhzDgOyznHv6Qc9NDvRVTGFvRRiXkPEXIXIZtPejjghsoNvfZdwLf22i4TnMfsmbBKTMrurSNy5Gp/nNKJp2F4HT30pQ/bFja1a5Qg3TmgV4OUxhWwJnBWanxfcer8B77qW5r8LBNCf1CfrP8+SR4uS9usRBXFQyz82Xu14RKlEM59LMnY9wQ4W9HCnBlBMGs8914lxbw64tw9wD8IZcgp6uv5IB48dMK2VOCrYbFl0oFD/7S7neV0n5A/+ivW23O75dQFpVo28q0K58W2eRylF3OYqLx9/x1nf/0p+6Rz1O8Zn6h4hjHrpt/i5+PRbn8cke59FuWcFX7ctjlehgay2f+1/T/nk2acstz9kZpXJr5y9r/PVOOWnABXFx6I4wO7c3BNy/D/wjp4Hqa7FHRVKWDud/gbPuX/+6yzr8qcB2y2i/S3CIuPqsKZIf7wh94uzl54AFjfIleWWQKBczaTY57snvMKZgXIEOSRT+ZaqYi8ogePWKGs8qZca17MK+xgmILl7J+DMXxJ0j+Y1HBf4DawlV+ftgKjzuM9T4s03c8LPoB2VeG+/3lUZuhlWT3RsqzMN7K3o+9eyoKo91D/NnLanCqLO5dE+7H5CUXN7TrnmVuOF2Yw3+49zfAHKt06Zxpi4RJ34Dw2Grz8jA0+c0smnQl/jFdLX/wZqxx/sIyZdU9LE8hX2GHjDS9AyfEGx8EO3fZqxGbezuUhw+RD3Oa/ip+YmFiVE88cMD0fPfew82wLGfj/er23+eR+QvuRri3drOUu0/7rCBR5xS+r/Bx4sxYL9mY5kR0u6z/Gdu8JCZ6lLGCED4KtDw4YgL2bSb2358tRM6nniuGymM+MiteXqYvQFe2vU/P6MbAZXggjn3JVhQTFlSrIIueF9xzX1i7D+5YAd8Ix6wgv4LjJ+wZMF+h8hxDxee+9pcXYNNGURk+Ptyvym8MG8KIVnSWfD24qRne4MpOrcWXdqPybP7dTJsYVmKYYwo07WG9npuNxfUdMSbw8JsrgudGOgLPN6CiOROXLFcxZZzhgz232vy9TpHH9Nra4/B1Zv7Aq5bx6YXGQ7xJA8vsfri5B8cGhPs7OP8Xzte5KfQU5lvU/Mb4VPbes6x5UhjxYLmoyleUSPN5fxpQTTW+Cw1Q+G05JggvONEGBqG5WrxxR3ied2UAVyHd+Tj/LFifdgpu0pvCkxg304TEZC7nKIyv4h13YxnyuXVXpwmsdQOlPJk/1mwu3g+fcuhHodfytR/LhMxCCeWRfg21w9kYP+Saj6mflezHj4G7ZSXNVi73QULaHRk8sExX5R/jTLs70Dp9Dc7fwLEAt6YWTNwuNJNI5rtRR2xFGFpuguKYxzH2/H2TYA0WLOp7GMrDEeF+VZhDWNNb4d8mumGL3GT2cm7Xba2Za1HyYAnDE85UhdlZIgd/kfwHaEPhun2+Qnzck6J/lA13bnPFJ+P6Ixi6ehIK058Hy1SmVYZ+0F71avPpkoyFKZDpNrrf8XHzPQb2QK0SN5ndXbo4YzgpSe9xazryNrsrDrMV5GN1uK9nY9LHFPX1QUhMGM2/ojP9xJsxrMDPEhtyKrJn9yzMG8IaM7+ppD0NEcFPbu8Ta0Le8FPN4ainOEUrEHgQQLs1BPS8juYujhTaW80FiyayvRc13LqNYlujcpcFnAzY8/gYZi5gQVLrex8jrf1M+SPD/gpiwFlYNDEFh/YYN26kdwkq+o+EBWUtRUALsYZuUZ3TX/Q3sgzyqIwLjYUZFThfCh5UaGn6TSsEd1IphZZ0v2njZCLQ1guq4mbP3eIshN/CND20uTv2wh69rjuwqPC/UJ6fi8eOfc5a10t4eiYefgTKA2P/VPDR5TOkb+lq827+BratRYQW92727T7zD0YcGtegh8BGic0kimLl7Ns/Nt58D4JuuVivPG/a86xuo4QbLGaYTOFM/XAGh+FknB4TfnsenXbyv8w/31toTjLO7Xi242TvN00Z52XkrymPPZotON8WHpgfeXuV19EKBAXBIm1DIMw8VQHtHgA27dDoyvaeP6+596Km607y3mgZQmVCMZ7M8Ow2vxGZ9P2u1zHiPQ+E1YwDC7xH4MYiHD4D2dPuZ9pDY4G9De4aC4dQAKyYuzuZ/sR6AbaQcbqNgjutT+9m+4ensWGmdfS0ENY+ex/sBTHM3NoE0k6lTJiyuBsvnm+lZfDidZSxvhKcfBYJRgUSYyw8JyFdPgIf0bJ2eTa0MEtFuCEuJGnVavPpQvzUUXzG9g5s+TCxMlhw+Wy65Wl3jMkn3ZO1mxci3NyTKtY9nXkZ/CCUV5ZOVOm/Vphv3xt1zyHxKeOkovynCOdAzTMtGpFSvQJpDx22Fa//CNayEKcqjmQHO5gleB4WNtL0MAOLzqIVCC0HYwrEfvd85gysyaV4/zEPG1dxxfhoKBDCZabkmYUQjMjaVaB/BtDrkdHJzS9GwsWoG2C1DLUyhJXxi6lgQkbLbkUlcwvdcqilYmZO8GJQp9bGcDFhtngeiUp/P7yUvt+GwnoJDXpBZDQBfi/AmWtn2JiI4JbdBf23GXZrKTGwZRyhdNIvYu9Fju+z5xXSgE8KTYhzthoXq2HD8m2h5dxN+sW8XWO1ec61KxT0q3ko/tuQtvHvsFjwg5Uxb3/XP8smUK5c3AvsDv5Wlu0DoaYMqsLM5wwbZ4kux5k9yyo3sNLF+EYnvuW++RHIHM8KtqwR0MWRq4BYB7leLSwxjc0wLOzXL0coLR3sWbezud/gLHDYz3ox3Vx0uV8V8JYztI5cAiO0q8LN3iGaVEzBae69d3NIM7AY3rKDlYEdbInxoH3Gi5UEpwPuQq/jMzifCsD/B/duhvIgbGZ4SwTcnismZQVDExv2YC9db3s6zZjyaL28zlDJRRvmZ4Q5OwzOH0VXqKei83jeF3uRzHcwyBDnh2dVsozr7VBefI9Ry9gwFtxydg5XptO4cucvS/8z39L/2HLp7MNBa7V5zbT1/vB74WntXi3cpuzNbw4xMm7h55BPqdu6ZH4NXywNTg9uYhkXeeHzUJZdBIVEJcY6qEwe2UDhOlEb3f/O8uufZBnrZV5bHRasFp6MNxev0pT57210+V9VIJwztuIwptzkxm0Iu1VCPXuaDwsWENapIPJOu10TDiEybmUHlYQdDAMPTgXEXG+MzaaNC8H1BKc4zlv+DTdkRdA9amrInk3jMgyG4x8RAhGbds4d/t1c013/cW1Nn00frOl3KOzZC+kuvEupFR7md+u14fxeKiQIqCro9MPxgq+38IdbhFi+joLWOowNY+GmGzLseFj9I6o8QwwbS5xOdznKdfVq82J/LQ/Z1NHAvdhy7m5glP0CiYNZRc6Eezlbw7zMEuapnTW8CFzxXoqLJv1qcot/vBh+8dC7fTGUx+/AIZmxDooxYJXeGSzmeVk4jgnZLyYd4MY5Wx/jcZmdqMq7/UIzRUHK6r6E7e4/Z2Dtv3QSM7A4nW0QhmApCV+natyAROLeQsUmTVGI+DxlS5HDBT91x4KF33X7WZkrP6vDNkqzu3PmbOP4DDDgrAyY69K21a913fXOCTV3MFbJtG7Ygy5nV9ihLF+MXsTf4x3Gd7mnFb8l0sV+x224GaPXfPeB72dcCjks6FQeMQXdFWyMJ/TMy4axML/kq9h9mBUGy5vla1z2bRgXlPt0Hb6x8mZK47BkH1Kt4nJxj5Bj9vgNCu6KO/MzGFPsaJsl7AEdwujjqMpbzFN4YcvGc/MPcP2XqF/Yg8G4e4RhHLmZI1eur9306wD+weCqyl9a8+HL0Fs773D/Er9z9MN4PjwiJGYlKMTEFIjJsOfR5ygFAnAuohiXXQHJi3BQy1kgcNmD4dvFxrzm5NIlMQUzxgMP2s2VTp8HTf91JNbU6W4xcvyLKV9hVM/qiJM467b8UEx2ZAhITMal1ZDGme/qtnoFMxIdn6lZcFKuzo7+fDLjxvy5ABcfQ7zP2HrN+XdDMTCv2y68HREIPQ72PrmnFGdvcZv390F5sLKIVR6UGbg2nQLJ9SY6/Cv7YcNYUGC/xGysLyHe58A+4zOIckKmlIPV5skr/Qe6Cr9tDiuVxqdPkkCBOBObp7y7tPGz4I55jPEbvmHFy8rcKa7bNsBDKARXQcf47ctC1rwEMn4IGdx5gnmK8WE9YTzaspzicHkC61Gc8nge6tIrYIHKh/ZjmHm5Kb4bTzP9fUXwt/asMm5tf5uTGc8gWG+f4hRIazw6hZZz4Q2BbguqdcUmXhMdjyX7NvD9DrxJR2eg3eKsJSpnmWGC4PRPg/KofjHkZ32ESjIkdDszIFBz37R7j3jJ5tcWMFKxGZds0MpKfUul4Kt6FDYMwyFTynXbm6y5ZD0C/Gj8DGlc6SPDDffZ8dhD8WtYG/HbU74DwudkQJXI6cTc0LCJdw8ZFMej8OADeLQGz+soD1g3hRsWZ/WocNEjoCxG9l8QlnNwybAOwjA+89BD4mrzH2CIzX0RsbZg3zCbTN7HZKk9hdd7l2W2pqK29306CHkjWQc5T8XhYVcLZRrQ7jzkmk+jIn8p6pmP4+wbqV5ZUDaNLWDkuYlhrwPw0vxtGGl5FR4xX1NObJqG8BWsGPd+Q76bCWc9kCq53u80uR2huIOBhQl++B91/scpkJbE1hYmPXtoolJsooj1H0k2Hw1FKJM+FQgThXtS3Yie4lUuUV0FYr51OftWAwvV7mwsQx0dIhlbKfqMhsVazdSvfkXF1ne61wGdq+C+DHdUID6d44S0lAh6Ev+D4ai/Qyb7e8ThJg5P5UUsPuWyRZgR+ES0+F+OsnQWnrGFGMvJRAVeyVZQ2sibODO8tY254zAWxtNug6QjIKRO3Iv8ZHyoPD4H5eFWm4NDf42kffY6CPUhhrWdsXwWfnY9WSXLIWOaGc1Tbf/Sr4cmhIXHBabiH+OI8OLld5ZdiXrm+fj9diiIdWFBYzu9qVDS9DjYeQGmDL8E9h8Gu0wD+hfLClbNLj6XO92E8NSaCWe8uShxJ8LX1xBirAIJYNJVIb3rAJgebd7BDKyJpQfsSubP3yvZ2VGei+2X37XCdXVY3Oe/NlbuZk95amm1OkTYMlBV/M3erduufeN2DvPUnKVTJb/yOSpSC8O/wfLrcNQdxmFhZd5YCAh/jLz7anyF8nr0MjjEhJXMyTzcx9z55gl4ZhUh7ddVHnDiwkrWt/lZVLzVm8kNYz2IsOKriMnLIMlY9CKUbsnuF5DCYbl+DeNJmUfgwBoZZ3ivytANK9adODYEy7w3k4bpi2p57FqsX2HPmuWC92IVicUTa9eaz4A7HNmtqIh/gGuuqaAsTr8+Hg1j1Jducg7j2EuegjOkmx+dsSE/H34+MeWSZSsRhthXC4F36oelfbzzMr3kyP+VCoQVB7v4yMj8AKxVQgYx0ptia60ZWIzSQCS2to8o9nAPvIu85zIM8FoXN5ZCyGiJy7hYu8QC1remj/Wc9pDvnH84X43eAYYceh5WYlzY2p4PHOzJ8JhqWIhoj/GMrUzyMgIvP9zHGVhTezp5y1XXNoyFIH0adqlA+ikhDBtmXaWvnbrjcVU4ujwPYWnNyIytHF04IBPfiee+Ts4EbuHXsE/+PchYaGh+DHz/Al4y7eukOePPg/FGluK7xdb7RdxqGcaN+Y7Km0dd43llyS+x1GFjcJznFdKhVbYj4+Gc2USPumHqsB8NDa3BAwGJM0JoLOD+V4//m4sW+Qqpf2mIB4Sk6c0hKHnIPYZu93DW3kG5t00UAfImkkD6959KPSBFw8UVPITj0h6cmxOG3RpLLGQs1HYwD1rBo18WT+Yh3o81Ls/hn8uD/fKCe+c3vuP+dQSJSpxltU54LNyMZwPJx9Xmn3Wlhg8AABLLSURBVKRi21K847HZjzkbo1XIGbTv/kU4DPaway6/QWHvDyIcDtiK58h9uLiDb7uXUNcb5hdLF+ajqXmK8pjvjBd/10lDzyvFu4qsWbauyKbnU36VQQ/QWRnIEGKlArF59GjJLoe3tq1EHkhVgKc/R+nAVwgnB7SJIiEblFuDZ7EZenrYdqM7NuzEMX5E68gQtdi0M3vrg7tZYcpeCHu/OP87yiHXRrDQsqD2Yhgn5nkWajusEsjLs0JeWT5yjoyXDTXkHtW/tGEsLuAE+C8ECRauWIG0j3imN6DIcQgw4tvmUaItL9h7tShHsGTuLE/V4RvrR7W9Vi+E02LTtwUHVAC9GsaD+WhqnrI8QbmMO9OjTpwDr5SfsfUzyPx7W8qjsfywyv/sUFThVseJ8riiHuUnuz08sTTpsBj7ozoyrT2JWi3YfkD7cOHtYDZv/mRz6WK/iWL/M7Ao9y5kBrYmaPqC4kXM/f+m/BvuRWeGHqQz+UxdFkkWCBqrEGeNqb28RyX4WqTxbQgTC2r/+ZCxm24ol+WC+cm6+TFxD2UpzJbpcQZWPjiIbzDZVeHC0sQeVJ2Z1myuvZIr4/mdkAG9x7KQxVZcU8NpXGPz4lT3/f/mgmD/AvnvMPnmExDIPDXev+BCCazoGVeuT/s+zlbxF1rO3TTOVgbb9bXNwPLr1I4Kbqp4enn8jG3xtvA5r+Mu2wHqZr81hTc7LlixSHVzUX4fyiJtTmAK797z3Aysdikpd9f9aQiP6xb7hTbd7e5ZT9rKfyUivhCHZeQqDpbG9yB5NtGyVeJVDofxnJUeh7I4fo+AvRBl8QH4w8p0kEqEcWYFArnpDc0sPR43PojfNFX+0C0LL75Dkm7EGb+40Ls/A+auooFM7oF0Dw76ESuXvTQEooFdlC/CVGZ827z/oSvbvI9TU/dGUJivaBiuGGP27AVubFxiZPdiJ/ifvgwVO96xudl3g1YiTAc/caAx9hxcPx95w+rdqvgbLxtumh5H/xnb2FcLFt9bMQOLn6HobZ1cLhQWkdytaZfOU8TE9pq3SE2zGH2De2DxM7ZjY2NsH/VpTMAGJ8cvtLF7fYqe485byr+12Ktuy6c1o2hALdeegYahrHk4X43EZUHcioNKhAW03/SmgiAbTN9Nv4gO8mlosbMxYnkel6Um+J9uRsfD9YIHoXAxcYHxoy76Y5w4s4fhjCl/tMehK6w2H3srrtkAqFKCtBZjvP/s1WbZIcFBTJholfUNWa3nD5h+081L6fV/a1gIlWmSnpVTIsxTsWWlm+90TzncUw95In0Kvmb4efw+LsQ6Ji2tfi5SuP5ZhlmE/suKZBmbDsbf5MNpb6ZSAAtuEG3jnbGBLA3RAD9ja5nQIA8kfKWBn3sP9w1BZuuKmbrqcK0wvHi9lu744pXn2TbIixOuJ4L9m9CyPgXh+REODj0wDzBOdQp93g3jhxfNyWU4noGvK7ueLKbXHIX7NCwDZcx2Olv4iBS/JU5e/SpcDjdxnQbWrzwdGfoNQX5leYU9xgvxSR/AxctttXm/4Qn+50+L8QMK13Gp4kN2zFMYfmhgUkZrTQPDOruGC/3cUBa2NU8bT8HxMYSPeYr1COPFIzactGdu4B5y0vRLkHki3mF8A89oDvfisFFreZ7yvJIUebE15Tafv0M9h4WcbIRz49dyeUgDZ4dhGNgQYmmG5EtY+obtIPbHCRF3JgQ8/OrxFD5jizj1KKDtzEuYnCxaaNO2tWdeWcb/Toj+XjizcJQdrEwx3OXeJ/013SF/mhz+nFUTeiJjXFmObZxPQsD+AmF9CIGyQs+wllVoViEw3zjFAffroCyfiKGeCxDZFPF1eQonyqUp48VnHB6k+Tj/9dupdlPnMdwUdgL+W4hkeFh5uHDhXGZcJQMIXG3+Izd0FVb1lzmq8cznBe41l6brESTEnz0ld+Z1wUF+7j69uTxs68GdIrysGp4Pxaopkay5E2E6B/F6No6b4VfIHy3+ZY0ISx/UqeCRYuPDNP19XD89eWDHnYn75okLPSp6JmNG5QsuXQ9fN6fp58Dr7uTDG7vxOtjJyzKGtUwenmUo/y4LfRN2afrmTw+7mtw2GAhkawZWV/tRD1i6BvsZWyYYkrqBzOxM31Ci4jEHLFnvEWfuuPpUBPl4HGzZdGs4sBDw2Q5krS/ynYOtA8K9kTFBiSCc2S5U+m9ZejI+pJSm56DH8DwEklMaWZiqDNYhpF9CZvkoavyv8v0AW/3Inv7jZt71n6Gn88MqQf559h2E67O8Nu5x7qbbsjU3WHj1bjw9CgcrrtKyGqRQOfLrkVxt/n6kOWev0e3gjB/2Sd003Cs243sW2ZOrhZveS7+NyvBbYeydYR0d45VIirCxov5XbA/yBbRDzkb8XoSy8CQEFDMZs27lxsfDfXAuQ2MtvRLHpxDXrU7ePgu5F1bopaYfR3UFWenBydi88rSZnPi5lwXxjTHXMMgBC/zgV4rvgKQN9gjLTeaU1tcRv68MKg0sZQs9DhmQiwhXoXBeD0vMxKZpC91U3GTl3sAMrIlt//esRnOfvbmvTIWT0sd0jDhgd800PQZgNgFMX0vzS32bow9tOm+d4FvvcwhDH3WCUWqXYUQjh5WkK0zIp8yfVCCPx3EsjgPwGmg+8hhz2Q5UAPcir2zEcX2z2fwhhpu4Gj1hPseJC2b7qtR64Uz/84ZxYKWP80tQ5j6MZwxTjEIMZSH5BTpRJ4Y9xMhmasWT9673a76ApanbixjAi9veAx3p0s9sam+QuHbTcrg8CQfz1hE4FuCg4bAR3p/gs7Cp2//se3jPgfcdMJ4P81VbjntQ898weA1QZmmrJvcicAOi/WMcjw3RL9fEXRlhUsjkRDp50EE/aS5dcnSyc+e+AN/VdsQDX2i4MdhsfVsgIpCzbYVKABUStynn8EylYZKwJT60yqcyBHEWgnJz60QQ5gYWTfJriT+Aax6Vhu8qyKOb4qBiodxKQbAAOdyUsS8FRP8gA8rj0uOxQdxfBX+j/GcQcLAx9eqgPDDM1194gv/FJ3sB7SvbYjvT77IyHY5Cm+5X73f8FF/2RnyDmQ3TxM1GvKpSKBuwvhfPL5ZOzw9escQ0CMyr6TLsCc/15dlGj3kpPV9X1t7WqkLmPga+/AkcYO+b+jM60K1mBt977P4Hrrv3tS/fhCGnf0SpY2aKLSBFkSRcjjd+LTnviDMdzLotoiKpujcnCYQeCfM0K36avLp0d5wCZY8ECnKUelcMOwPMHhUuvoKgn4afPn/zQbnhUAg3SvwglMbL2VgYqvIoD8vu99QrBaaPSyOcp+WrcM/vvrv7Eegao9IeCF1ZIUNv5CZcv7yrpJgH1Oj8ItfYq/Cy0jVE+lUgISHdCy+GgMqoXGPHhFN25iSBkFeZJ1zmmkuRgNpgpc/ex1sQfCoPpxQi4sC4ohxzWC57Pe2jrM65+EfEc/aszIVe0yzRqVQgFi5k8JQtG7Ts0G/IK2CzUX5mu2ryiv+H78U6w/HpQZqbgjBrIQxStmSJwFAJ8OW9Vx4Xn4GhqwvhGQtYbNlknufeDq/mOx31PoaaVBI+hUBsJuU4MVs1/bVs3re+mVxzPoOwOoSj3wrf3N8c5NXXbMGhTiIwGwQ4dMUZYJiyewAy8wehO5xCQFgsb5cFKwxdcbX5BV/DO53ePhBV5oOeiUAJgWgFUiIj7pG9+V+7eT/ooSODo5hCUibfXkatD5akQMpo6dnIEbApu+jZvwPK42gEMHboikO1KL/t1eacRDByEVSAdmsC/bzArgvGK4sUCxKzjOtKaPpRIEFZYKVm2uAsCRopEM9B/+cAAQw3scfAWWQvwsjweQhyUAqVgWc+R+OJq83T3x/iavPKgMjCnk1g5hVIlq1AxmfPh8NhA1AgyQYsanJbT9Sek75np71iP4sEoDTClF23xooLBmliy6MbSoYWwWrzC/iFRa4d6W942fuv/yJQi0Bshq0ltItlUxYrQkeh3wzvexvum9Hwsd589C5B1G0RGD4B994DFT6VCArFh+Ajtwpi78PKSFkgOMTF3sdnoDSGs9q8zHc9E4EcgZl7B9L21IavqAC8Emg/q3NFBcR3ILcERzGFr4582RWBoRCw9x4Qjmm32Zk4c3sZlsWq8mBDXHdiHQu+jSIjArNLYDYUCJf+07DC76fSZyuMcr7NfzBVhc/b0n8RmEUC7H3wvcfiU98+P52YeEkIChtCMWWB9mj1NTOy2tx7pv8i0JXATCoQG7L6FFaOn4fqfhWKzEMoDDEFZ0oE3Hpi7IDqPnFq2wuY/Cl29VMERocAFztSiWy95vxxbNX+KWT+VyB03FOpwqTsfWxDK+lyDF1dheEvrTavIKbHwyfQQ+U9gEBxa+MFC5b0JWnXLpQlbI6nVaJ9YZTj2SWw+ORL9sGsxNCz6B4WbAqZoan1IF+WhxfwajB1x6Unuy0BrgfxG4D1H0XuUTMoWf2HRhJEoBYBKoI6DthzYc+jjhvZFYFhEpilHkjYCrrfmGnjxH4Jyv0sE6BSqBME25uujhvZFQEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAEREAERGCqB/wWFK8PQzzsEqgAAAABJRU5ErkJggg=="
                />
              </defs>
            </svg>
          </PaypalButton>
        </>
      )}
      {!implemented && (
        <StoresWrapper>
          <StoreButtons>{STORES.map(renderStoreItem)}</StoreButtons>
          {planDetails && (
            <Disclaimer
              dangerouslySetInnerHTML={{
                __html: t(isShortDisclaimer ? 'short_disclaimer' : 'disclaimer', {
                  nowPrice: `$${planDetails.periodPrice}`,
                  disclaimer: t(planDetails.disclaimer),
                  fullPrice: `$${planDetails.price}`,
                  mail: supportMail,
                }),
              }}
              onClick={(e) => onLinkClick(e)}
            />
          )}
        </StoresWrapper>
      )}
      {isStage && (
        <input
          type="hidden"
          id="test-skip-checkout"
          onClick={() => history.push('/success')}
        />
      )}
      {showError && (
        <ErrorScreen clearFields={clearFields} />
      )}
    </>
  );
};

export default CheckoutScreen;
